/// <amd-module name="Feed/Legacy/Protocols/Common/DeleteCommandHelper" />
import * as Utils_String from "VSS/Core/Util/String";

import { FwLinks, PackageCommandIds } from "Feed/Common/Constants/Constants";
import { Package, PackageVersion } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { IGeneralDialogProps } from "Feed/Common/Types/IGeneralDialogProps";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { RemovePackagesCommand } from "Feed/Legacy/Protocols/Common/PackageCommands";
import { MavenKey } from "Feed/Legacy/Protocols/Maven/Constants/MavenConstants";
import { NpmKey } from "Feed/Legacy/Protocols/Npm/Constants/NpmConstants";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";

export class DeleteCommandHelper {
    public static addDeleteCommands(
        protocolType: string,
        selectedPackages?: Package[],
        selectedVersions?: PackageVersion[],
        packageName?: string,
        disabled?: boolean,
        cachedPackagesIncluded?: boolean
    ): IPackageCommand[] {
        // Must have npm specific code in this file because npm "delete" is "unpublish"
        const isNpm = protocolType == NpmKey;
        const packageCommands = new Array<IPackageCommand>();
        let commandName = isNpm
            ? PackageResources.PackageCommands_UnpublishPackage
            : PackageResources.PackageCommands_DeletePackage;
        let headerText: string;
        let confirmText: string;
        let severeWarningText: string;
        let deleteDialogProps: IGeneralDialogProps;
        let id: string;

        if (selectedVersions) {
            headerText =
                selectedVersions.length > 1
                    ? Utils_String.format(
                          PackageResources.DeleteDialog_HeaderText_VersionsList,
                          commandName,
                          selectedVersions.length
                      )
                    : Utils_String.format("{0} {1} {2}", commandName, packageName, selectedVersions[0].version);
            confirmText =
                selectedVersions.length > 1
                    ? PackageResources.DeleteDialog_ConfirmText_VersionsList
                    : PackageResources.DeleteDialog_ConfirmText_OneVersion;
            severeWarningText =
                selectedVersions.length > 1
                    ? PackageResources.DeleteDialog_SevereWarningText_VersionsList
                    : PackageResources.DeleteDialog_SevereWarningText_OneVersion;
            selectedPackages =
                selectedPackages && selectedPackages[0] && selectedPackages[0].protocolType.toLowerCase() === MavenKey
                    ? selectedPackages
                    : null;
            id = isNpm ? PackageCommandIds.Unpublish : PackageCommandIds.Delete;
        } else if (selectedPackages) {
            headerText =
                selectedPackages.length > 1
                    ? Utils_String.format(PackageResources.DeleteDialog_HeaderText_LatestVersion, commandName)
                    : Utils_String.format(
                          "{0} {1} {2}?",
                          commandName,
                          selectedPackages[0].name,
                          selectedPackages[0].versions[0].version
                      );
            confirmText =
                selectedPackages.length > 1
                    ? PackageResources.DeleteDialog_ConfirmText_LatestVersion
                    : PackageResources.DeleteDialog_ConfirmText_LatestVersion_OnePackage;
            severeWarningText =
                selectedPackages.length > 1
                    ? PackageResources.DeleteDialog_SevereWarningText_LatestVersion
                    : PackageResources.DeleteDialog_SevereWarningText_LatestVersion_OnePackage;

            commandName = Utils_String.format(PackageResources.DeleteCommand_Latest, commandName);
            id = isNpm ? PackageCommandIds.UnpublishLatest : PackageCommandIds.DeleteLatest;
        }

        if (!disabled) {
            deleteDialogProps = this.getDeleteDialogProps(
                protocolType,
                headerText,
                confirmText,
                severeWarningText,
                selectedPackages,
                selectedVersions,
                cachedPackagesIncluded
            );
        }

        packageCommands.push(new RemovePackagesCommand(id, commandName, deleteDialogProps, disabled));
        return packageCommands;
    }

    private static getDeleteDialogProps(
        protocolType: string,
        headerText: string,
        confirmText: string,
        severeWarningText: string,
        selectedPackages?: Package[],
        selectedVersions?: PackageVersion[],
        cachedPackagesIncluded?: boolean
    ): IGeneralDialogProps {
        const dialogProps: IGeneralDialogProps = {
            headerText,
            confirmText,
            severeWarningText,
            severeWarningLearnMoreLink: FwLinks.DeleteDialogLearnMore,
            saveButtonClassName: "delete-button",
            dialogClassName: "delete-dialog",
            saveButtonText: PackageResources.DeletionDialogContent_OkButton,
            onSaveCallback: () =>
                Actions.PackagesDeleted.invoke({
                    selectedPackages,
                    selectedVersions,
                    protocolType: selectedVersions ? protocolType : null
                }),
            onDismissCallback: () => Actions.DialogOpenChanged.invoke(null),
            onSavePrimaryButtonText: PackageResources.PackageCommands_DeletePackage_Deleting,
            messageBarMessage: cachedPackagesIncluded
                ? PackageResources.UnpublishDialog_CannotUnpublishV1CachedPackages
                : null
        };

        return dialogProps;
    }
}
